<!--
  @project E-School Web
  @author Vothana CHY
  @date 2026-07-13
-->

<script lang="ts" setup>
// Section 6 — Best Lecturers
// Reference: 6. HOME.png
// Layout: white bg, centered title
// Cards: horizontal row of pill-shaped cards
//   Each card: large circle avatar (photo placeholder) + name (bold Khmer) + subject (smaller Khmer)
//   4th card is partially visible (cropped at right edge) — indicates scrollable
// Colors: card bg = very light warm gray (#f5f4f2), border = none

import { ref, computed, onMounted, onUnmounted, nextTick } from 'vue';
import { getLecturers } from '~/constants/lecturers';

const { t, locale } = useI18n();

const scrollContainer = ref<HTMLDivElement | null>(null);

// ─── Auto-play ───────────────────────────────────────────────────────────────

const autoPlayInterval = ref<ReturnType<typeof setInterval> | null>(null);

const getCardScrollStep = () => {
  if (!scrollContainer.value) return 384;
  const firstCard = scrollContainer.value.firstElementChild as HTMLElement;
  if (firstCard) {
    const style = window.getComputedStyle(scrollContainer.value);
    const gap = parseFloat(style.columnGap || style.gap) || 16;
    return firstCard.offsetWidth + gap;
  }
  return 384;
};

// Flag: true while the auto-scroll smooth animation is running.
// Prevents the scroll listener from resetting the resume timer mid-animation.
let isAutoScrolling = false;

const stopAutoScrollAnimation = () => {
  if (!scrollContainer.value) return;
  isAutoScrolling = false;
  const current = scrollContainer.value.scrollLeft;
  // Setting scrollLeft to itself with behavior 'auto' halts any active smooth scroll animation instantly
  scrollContainer.value.style.scrollBehavior = 'auto';
  scrollContainer.value.scrollLeft = current;
  scrollContainer.value.style.scrollBehavior = '';
};

const scrollNext = () => {
  if (!scrollContainer.value) return;
  const step = getCardScrollStep();
  isAutoScrolling = true;
  scrollContainer.value.scrollBy({ left: step, behavior: 'smooth' });
  // Clear flag after smooth scroll animation settles (~600 ms)
  setTimeout(() => {
    isAutoScrolling = false;
    // Check wrap-around after smooth scroll finishes and settles
    handleScroll();
  }, 600);
};

const startAutoPlay = () => {
  if (autoPlayInterval.value) clearInterval(autoPlayInterval.value);
  autoPlayInterval.value = setInterval(scrollNext, 4000);
};

const stopAutoPlay = () => {
  if (autoPlayInterval.value) {
    clearInterval(autoPlayInterval.value);
    autoPlayInterval.value = null;
  }
};

// ─── Reusable auto-play pause/resume with swipe threshold ───────────────────
// isDragging is shared with useDragToScroll so mouseleave during a drag
// does NOT schedule a resume (fixes "keeps animating while holding" bug).

const isDragging = ref(false);

const { touchHandlers, mouseHandlers, pauseAndScheduleResume, cancelResume, notifyScrolling } = useCarouselAutoPlay(
  startAutoPlay,
  stopAutoPlay,
  {
    resumeDelay: 3000,
    isDragging,
    onInteractionStart: stopAutoScrollAnimation,
  }
);

// ─── Mouse-drag to scroll (desktop) ─────────────────────────────────────────

useDragToScroll(scrollContainer, {
  onDragStart: () => {
    isDragging.value = true;
    cancelResume();
    stopAutoScrollAnimation();
  },
  onDragEnd: () => {
    isDragging.value = false;
    pauseAndScheduleResume();
  },
});

// ─── Infinite-scroll wrap-around + momentum-scroll guard ────────────────────
// IMPORTANT: We set style.scrollBehavior = 'auto' before the wrap-around
// scrollTo so it is always instant. The container does NOT use the
// Tailwind `scroll-smooth` class — that would make even 'auto' scrolls
// animate (respect CSS scroll-behavior: smooth), causing a visible fly-back.

const handleScroll = () => {
  if (!scrollContainer.value) return;

  // Guard: If we are programmatically smooth scrolling, do not trigger wrap-around or notify scrolling.
  // This avoids race conditions between browser smooth-scroll and our instant wrap-around.
  if (isAutoScrolling) return;

  const container = scrollContainer.value;
  const cards = container.children;
  if (!cards || cards.length < 12) return;

  const card0 = cards[0] as HTMLElement;
  const card6 = cards[6] as HTMLElement;
  const setWidth = card6.offsetLeft - card0.offsetLeft;
  const scrollLeft = container.scrollLeft;

  // Seamless infinite wrap-around — must be INSTANT (no CSS smooth animation)
  if (scrollLeft < setWidth - 100) {
    container.style.scrollBehavior = 'auto';
    container.scrollLeft = scrollLeft + setWidth;
    container.style.scrollBehavior = '';
  } else if (scrollLeft > setWidth * 2 - 100) {
    container.style.scrollBehavior = 'auto';
    container.scrollLeft = scrollLeft - setWidth;
    container.style.scrollBehavior = '';
  }

  // If this scroll event was triggered by the user (not our own scrollNext),
  // keep pushing the resume timer forward so auto-play doesn't fight inertia.
  notifyScrolling();
};

// ─── Lifecycle ───────────────────────────────────────────────────────────────

onMounted(() => {
  nextTick(() => {
    if (scrollContainer.value) {
      const cards = scrollContainer.value.children;
      if (cards && cards.length >= 12) {
        const card0 = cards[0] as HTMLElement;
        const card6 = cards[6] as HTMLElement;
        const setWidth = card6.offsetLeft - card0.offsetLeft;
        scrollContainer.value.scrollLeft = setWidth;
      }
      scrollContainer.value.addEventListener('scroll', handleScroll, { passive: true });
    }
    startAutoPlay();
  });
});

onUnmounted(() => {
  stopAutoPlay();
  scrollContainer.value?.removeEventListener('scroll', handleScroll);
});

// ─── Data ────────────────────────────────────────────────────────────────────

const lecturersList = computed(() => {
  const base = getLecturers(locale.value);

  // Replicate 3 times for infinite scrolling effect
  return [...base, ...base, ...base].map((item, idx) => ({
    ...item,
    uniqueId: `${item.id}-${idx}`,
  }));
});
</script>

<template>
  <section class="w-full bg-white">
    <div class="mx-auto max-w-360 py-16">
      <!-- Title -->
      <h2
        class="mb-12 px-6 text-center text-[26px] font-extrabold text-gray-900 sm:text-[32px] md:px-20 md:text-[36px]"
      >
        {{ t('lecturers.title') }}
      </h2>

      <!-- Scroll container with fade overlays to blend edges -->
      <div class="relative w-full">
        <!-- Left fade gradient -->
        <div
          class="pointer-events-none absolute top-0 bottom-0 left-0 z-10 w-6 bg-linear-to-r from-white to-transparent md:w-20"
        ></div>

        <div
          ref="scrollContainer"
          class="no-scrollbar flex snap-x snap-mandatory gap-4 overflow-x-auto px-6 pt-4 pb-4 [-webkit-overflow-scrolling:touch] md:gap-6 md:px-20"
          v-on="touchHandlers"
          @mouseenter="mouseHandlers.onEnter"
          @mouseleave="mouseHandlers.onLeave"
        >
          <div
            v-for="lecturer in lecturersList"
            :key="lecturer.uniqueId"
            class="flex w-[calc((100vw-64px)/1.2)] shrink-0 snap-start [scroll-snap-stop:always] items-center gap-3 rounded-full border border-[#e8e3f5] bg-[#f2effb]/40 px-4 py-3 shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:border-[#d6cbf0] hover:bg-[#f2effb]/60 hover:shadow-md sm:w-90 sm:gap-5 sm:px-6 sm:py-4"
          >
            <!-- Circle avatar with profile picture -->
            <div
              class="relative flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-full text-lg font-bold text-white shadow-inner sm:h-20 sm:w-20 sm:text-xl"
              :class="lecturer.bgColor"
            >
              <AppImage
                v-if="lecturer.image"
                :src="lecturer.image"
                :alt="lecturer.name"
                img-class="absolute inset-0 h-full w-full rounded-full object-cover transition-opacity duration-300"
                loader-size="h-6 w-6"
                loader-overlay-class="bg-transparent"
                loader-text=""
              />
              <span v-if="!lecturer.image">{{ lecturer.initials }}</span>
            </div>

            <!-- Name + subject -->
            <div class="flex flex-col items-start">
              <span
                v-if="lecturer.prefix"
                class="mb-1 rounded-full border border-violet-200/50 bg-violet-50 px-2 py-px text-[8px] font-bold tracking-wider text-violet-600 uppercase sm:text-[9px]"
              >
                {{ lecturer.prefix }}
              </span>
              <span class="text-base leading-tight font-bold text-gray-950 sm:text-[17px]">
                {{ lecturer.nameOnly }}
              </span>
              <span class="mt-0.5 text-xs text-gray-600 sm:text-[14px]">
                {{ lecturer.subject }}
              </span>
            </div>
          </div>
        </div>

        <!-- Right fade gradient -->
        <div
          class="pointer-events-none absolute top-0 right-0 bottom-0 z-10 w-6 bg-linear-to-l from-white to-transparent md:w-20"
        ></div>
      </div>
    </div>
  </section>
</template>
